<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { cn } from '@/lib/utils';
import { products } from '@/lib/product';
import { i18n, languages } from '@/i18n';
import Wechat from '@/components/global/Wechat.vue';
import { Languages } from 'lucide-vue-next';

const props = defineProps({
  class: String,
  t: Function,
});

const wechatRef = ref(null);
const currentLang = ref(i18n.locale);
const isLanguageMenuOpen = ref(false);
const unicornScene = ref(null);

function openWechatPanel() {
  wechatRef.value.open();
}

const toggleLanguageMenu = () => {
  isLanguageMenuOpen.value = !isLanguageMenuOpen.value;
};

const changeLanguage = (lang) => {
  currentLang.value = lang;
  i18n.setLocale(lang);
  isLanguageMenuOpen.value = false;
};

// Geometric shapes explosion effect
function createGeometricShapes(e) {
  const shapes = ['square', 'triangle', 'circle', 'cross'];
  const numShapes = Math.floor(Math.random() * 4) + 5; // 5-8 shapes

  for (let i = 0; i < numShapes; i++) {
    const shape = shapes[Math.floor(Math.random() * shapes.length)];
    const size = Math.floor(Math.random() * 13) + 20; // 20-32px
    const angle = Math.random() * Math.PI * 2;
    const velocity = Math.random() * 150 + 100; // 100-250px
    const rotation = Math.random() * 720 - 360; // -360 to 360 degrees

    const element = document.createElement('div');
    element.className = `geometric-shape ${shape}`;
    element.style.width = `${size}px`;
    element.style.height = `${size}px`;
    element.style.left = `${e.clientX}px`;
    element.style.top = `${e.clientY}px`;
    element.style.setProperty('--tx', `${Math.cos(angle) * velocity}px`);
    element.style.setProperty('--ty', `${Math.sin(angle) * velocity}px`);
    element.style.setProperty('--rotation', `${rotation}deg`);

    document.body.appendChild(element);

    setTimeout(() => {
      element.remove();
    }, 1000);
  }
}

// Initialize Unicorn Studio effect
onMounted(() => {
  // Load Unicorn Studio script
  const script = document.createElement('script');
  script.src =
    'https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v1.4.35/dist/unicornStudio.umd.js';
  script.async = true;

  script.onload = () => {
    // Wait a bit for the script to be fully ready
    setTimeout(() => {
      if (window.UnicornStudio) {
        window.UnicornStudio.addScene({
          elementId: 'footer-unicorn-bg',
          fps: 60,
          scale: 0.7,
          dpi: 1.5,
          filePath: '/unicorn-footer-effect.json',
          lazyLoad: false,
          fixed: false,
          altText: 'Footer background effect',
          interactivity: {
            mouse: {
              disableMobile: false,
              disabled: false,
            },
          },
        })
          .then((scene) => {
            unicornScene.value = scene;
            console.info('Unicorn Studio footer effect loaded');
          })
          .catch((err) => {
            console.warn('Failed to load Unicorn Studio effect:', err);
          });
      }
    }, 100);
  };

  script.onerror = () => {
    console.warn('Failed to load Unicorn Studio script');
  };

  document.head.appendChild(script);
});

onUnmounted(() => {
  if (unicornScene.value) {
    unicornScene.value.destroy();
  }
  if (window.UnicornStudio) {
    window.UnicornStudio.destroy();
  }
});
</script>

<template>
  <!-- Footer Section -->
  <footer :class="cn(props.class, 'relative z-20')" @click="createGeometricShapes">
    <div class="w-full p-2 md:p-4 mt-32 overflow-hidden">
      <div class="relative bg-black/90 rounded-[24px] p-8 md:p-12 overflow-hidden">
        <!-- Unicorn Studio Background Effect -->
        <div
          id="footer-unicorn-bg"
          class="absolute inset-0 z-0 opacity-60 pointer-events-none rounded-[24px]"
        ></div>

        <!-- Main Footer Content -->
        <div
          class="relative z-10 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 lg:gap-8"
        >
          <!-- Product Links -->
          <div>
            <h3 class="text-sm font-normal mb-6 text-white/50 tracking-wide">
              {{ $t('landing.designs', 'Designs') }}
            </h3>
            <ul class="space-y-4">
              <li
                v-for="product in products.filter((p) => p.visibility !== 'hidden')"
                :key="product.name"
              >
                <router-link
                  :to="`/create/${product.name}`"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t(product.title).replace(/\s*Generator\s*/gi, '') }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Resources Links -->
          <div>
            <h3 class="text-sm font-normal mb-6 text-white/50 tracking-wide">
              {{ $t('landing.resources') }}
            </h3>
            <ul class="space-y-4">
              <li>
                <router-link
                  to="/features"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.features') }}
                </router-link>
              </li>
              <li>
                <router-link
                  to="/templates"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.templates') }}
                </router-link>
              </li>
              <li>
                <a
                  href="https://seedesign.feishu.cn/wiki/S65OwuCLSiCm2qkZHUhccFVJnGg?from=from_copylink"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.promptsLibrary', 'Prompts library') }}
                </a>
              </li>
              <li>
                <router-link
                  to="/docs"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.docs') }}
                </router-link>
              </li>
              <li>
                <router-link
                  to="/help"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.helpCenter') }}
                </router-link>
              </li>
              <li>
                <router-link
                  to="/use-cases"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.cases') }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Company & Legal Links -->
          <div>
            <h3 class="text-sm font-normal mb-6 text-white/50 tracking-wide">
              {{ $t('landing.company', 'Company') }}
            </h3>
            <ul class="space-y-4">
              <li>
                <router-link
                  to="/about"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.aboutUs') }}
                </router-link>
              </li>
              <li>
                <router-link
                  to="/blog"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.blog') }}
                </router-link>
              </li>
              <li>
                <router-link
                  to="/contact"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.contactUs') }}
                </router-link>
              </li>
              <li>
                <router-link
                  to="/affiliates"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('landing.affiliates') }}
                </router-link>
              </li>
            </ul>

            <h3 class="text-sm font-normal mb-6 mt-8 text-white/50 tracking-wide">
              {{ $t('landing.legal', 'Legal') }}
            </h3>
            <ul class="space-y-4">
              <li>
                <router-link
                  to="/terms"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('app.terms') }}
                </router-link>
              </li>
              <li>
                <router-link
                  to="/privacy"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                >
                  {{ $t('app.privacy') }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Social Links (Connect) -->
          <div>
            <h3 class="text-sm font-normal mb-6 text-white/50 tracking-wide">
              {{ $t('landing.connect', 'Connect') }}
            </h3>
            <ul class="space-y-4">
              <li>
                <a
                  href="https://x.com/seedeAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 flex items-center gap-2 font-normal"
                >
                  <svg class="h-4 w-4 text-white/80" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                    />
                  </svg>
                  <span>X</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/company/seede-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 flex items-center gap-2 font-normal"
                >
                  <svg class="h-4 w-4 text-white/80" viewBox="0 0 24 24" fill="currentColor">
                    <path
                      d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                    />
                  </svg>
                  <span>LinkedIn</span>
                </a>
              </li>
              <li>
                <button
                  @click="openWechatPanel"
                  class="text-sm text-white/70 hover:text-white transition-colors duration-200 flex items-center gap-2 font-normal group"
                >
                  <svg
                    class="h-4 w-4 group-hover:text-[#07C160] transition-colors"
                    viewBox="0 0 300 300"
                  >
                    <path
                      fill="currentColor"
                      d="M300 255c0 25-20 45-45 45H45c-25 0-45-20-45-45V45C0 20 20 0 45 0h210c25 0 45 20 45 45v210z"
                    />
                    <g fill="#FFF">
                      <path
                        d="M201 112c-24 1-45 8-63 25a74 74 0 0 0-23 63l-26-3c-3-1-7 0-9 1l-26 16 4-21c2-5 1-8-3-11-29-21-42-51-32-83 8-29 29-47 57-56 39-13 82 0 106 31 8 11 13 23 15 38zM89 102c1-6-4-11-10-11s-11 4-12 10c0 6 5 11 11 11 6 1 11-4 11-10zm59-11c-6 0-12 5-11 11 0 6 5 11 11 11 6-1 11-5 11-11s-5-11-11-11z"
                      />
                      <path
                        d="M255 263c-7-4-14-9-22-9-7-1-15 3-23 4-24 2-45-4-63-21-33-30-28-78 10-103 35-23 85-15 109 16 21 27 19 63-7 86-7 7-10 12-5 21l1 6zm-87-85c5 0 9-3 9-8s-4-10-9-10-9 5-9 10c0 4 4 8 9 8zm56-18c-5 0-9 4-9 9s4 9 9 9 9-3 9-8-4-10-9-10z"
                      />
                    </g>
                  </svg>
                  <span> {{ $t('landing.wechat', 'WeChat') }}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        <!-- Footer Bottom -->
        <div class="relative z-10 mt-16 pt-8 border-t border-white/10">
          <div class="relative flex flex-row items-center justify-between gap-2 md:gap-4">
            <!-- Copyright Info - Left -->
            <p class="text-xs md:text-sm text-white/50 font-normal flex-1">
              <span class="hidden sm:inline">© {{ new Date().getFullYear() }} Seede AI.</span>
              <span class="sm:hidden">© {{ new Date().getFullYear() }}</span>
              <a
                v-if="i18n.isChinese"
                href="https://beian.miit.gov.cn/"
                rel="nofollow noopener noreferrer"
                target="_blank"
                class="ml-2 hover:text-white/80 transition-colors hidden md:inline"
                >蜀 ICP 备 2025130505 号</a
              >
            </p>

            <!-- Logo - Absolute Center -->
            <div
              class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center group cursor-pointer"
            >
              <svg
                width="20"
                height="22"
                viewBox="0 0 32 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="footer-logo"
              >
                <defs>
                  <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#60a5fa" />
                    <stop offset="25%" stop-color="#a78bfa" />
                    <stop offset="50%" stop-color="#ec4899" />
                    <stop offset="75%" stop-color="#f97316" />
                    <stop offset="100%" stop-color="#eab308" />
                  </linearGradient>
                </defs>
                <path
                  d="M26.1904 13.3808C27.8786 13.851 29.2868 14.8531 30.2217 16.5039C33.9933 23.1639 29.619 30.7226 20.3975 34.3955C10.7276 38.2467 0.501269 33.9736 0.279297 33.8593C0.143091 33.789 0.0652346 33.6179 0.0830078 33.4843C0.103487 33.3241 3.11189 27.2784 7.87988 21.3974C7.96532 21.2916 8.13371 21.3258 8.18457 21.4511C8.25402 21.6221 8.33407 21.7938 8.42578 21.9638C10.1542 25.1589 15.9693 24.0823 19.4951 20.9189C22.3639 18.6805 25.3883 14.197 25.9395 13.4677V13.4668C25.9982 13.3902 26.097 13.355 26.1904 13.3808ZM11.1787 1.60544C20.8499 -2.24616 31.0778 2.0276 31.2969 2.14157C31.5156 2.2555 31.5265 2.40711 31.458 2.60641C31.458 2.60641 27.1427 10.3413 23.9756 14.2715C23.948 14.3062 23.9087 14.3548 23.873 14.3984C23.8214 14.4605 23.7348 14.482 23.6602 14.4482C23.5079 14.3805 23.2482 14.2643 23.0391 14.1806C21.3171 13.4906 17.7361 12.42 14.1113 13.5009C10.4483 14.5934 7.97492 17.034 7.86719 19.6357C7.86264 19.7371 7.82237 19.8361 7.75391 19.9072C7.68544 19.9783 7.61748 20.05 7.55078 20.122C6.79574 20.9403 6.10147 21.7433 5.46484 22.5215C5.41586 22.581 5.33545 22.6062 5.26074 22.584C3.62782 22.0996 2.26525 21.1068 1.35352 19.497C-2.41787 12.8371 1.95716 5.27821 11.1787 1.60544Z"
                  class="logo-path"
                />
              </svg>
            </div>

            <!-- Language Switcher - Right -->
            <div class="relative flex-1 flex justify-end">
              <button
                @click="toggleLanguageMenu"
                class="flex items-center gap-1 md:gap-2 border border-white/5 text-white/60 hover:text-white/90 font-medium px-2 md:px-3 py-1.5 md:py-2 rounded-full hover:bg-white/5 transition-all duration-200 text-xs md:text-sm"
              >
                <Languages class="size-3 md:size-4" />
                <span class="hidden sm:inline">{{
                  languages.find((l) => l.code === currentLang)?.label
                }}</span>
                <span class="sm:hidden">{{
                  languages.find((l) => l.code === currentLang)?.code.toUpperCase()
                }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-3 h-3 md:w-4 md:h-4 transition-transform"
                  :class="{ 'rotate-180': isLanguageMenuOpen }"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              <!-- Language Dropdown -->
              <div
                v-if="isLanguageMenuOpen"
                class="absolute bottom-full mb-2 right-0 w-[192px] bg-white/90 backdrop-blur-md rounded-[20px] shadow-[0_3px_10px_0_rgba(0,0,0,0.07)] border border-black/10 p-2 z-50"
              >
                <button
                  v-for="lang in languages"
                  :key="lang.code"
                  @click="changeLanguage(lang.code)"
                  class="w-full px-3 py-2 hover:bg-black/5 transition-colors duration-200 text-left rounded-lg cursor-pointer flex items-center justify-between"
                  :class="{
                    'font-medium text-black/90': currentLang === lang.code,
                    'text-black/50': currentLang !== lang.code,
                  }"
                >
                  <span class="text-sm">{{ lang.label }}</span>
                  <svg
                    v-if="currentLang === lang.code"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="text-black/90"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Wechat ref="wechatRef" :show="false" />
  </footer>
</template>

<style>
.geometric-shape {
  position: fixed;
  pointer-events: none;
  border: 1px solid #fff;
  background: transparent;
  animation: explode 1s ease-out forwards;
  z-index: 9999;
}

/* Square is just a div with border - no additional styles needed */

.geometric-shape.circle {
  border-radius: 50%;
}

.geometric-shape.triangle {
  border: none;
  width: 0 !important;
  height: 0 !important;
  border-left: 16px solid transparent;
  border-right: 16px solid transparent;
  border-bottom: 28px solid #fff;
  background: transparent;
}

.geometric-shape.cross {
  position: relative;
}

.geometric-shape.cross::before,
.geometric-shape.cross::after {
  content: '';
  position: absolute;
  background: #fff;
}

.geometric-shape.cross::before {
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  transform: translateX(-50%);
}

.geometric-shape.cross::after {
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
  transform: translateY(-50%);
}

@keyframes explode {
  0% {
    transform: translate(0, 0) rotate(0deg) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) rotate(var(--rotation)) scale(0.3);
    opacity: 0;
  }
}

/* Footer logo styles */
.footer-logo {
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.footer-logo .logo-path {
  fill: white;
  fill-opacity: 0.5;
  transition: fill 0.3s ease;
}

.group:hover .footer-logo {
  opacity: 1;
}

.group:hover .footer-logo .logo-path {
  fill: url(#logo-gradient);
  fill-opacity: 1;
  animation: gradient-flow 3s ease-in-out infinite;
}

@keyframes gradient-flow {
  0%,
  100% {
    filter: hue-rotate(0deg);
  }
  50% {
    filter: hue-rotate(60deg);
  }
}
</style>
